/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What is unfinished inside an expression, and where** (`4c`, `#1334`).
 *
 * The mapping validators check a **row**: its value against the destination, its required-ness, its
 * omission answer. None of them looked inside the expression a row holds, so an empty path in a
 * calculation's operand, a case that tests nothing and a comparison with nothing to compare against
 * all passed silently. Each is structure rather than a value — `FD9` draws that line: `#914` grades
 * a **value** against a declaration, this walks **inside** an expression, and the two share only the
 * `MappingIssue` taxonomy.
 *
 * Four rules, each from a ruling this file is an instance of:
 *
 * - **A warning, never a block** (`R3a`). The runtime handles every one of these — an empty path is
 *   unresolved and the row's own omission answer takes over — so a console that refused them would
 *   be stricter than the thing it configures.
 * - **Each finding carries where it is** (`at`), in the editor's terms, so its affordance opens the
 *   row *at that field* (pattern 38) rather than at the row and leaving the operator to hunt.
 * - **The root is the row's business.** The surface reports a row whose own path is empty or
 *   unknown; this reports what the row validators cannot see, and repeating the root would render
 *   one finding twice (pattern 35). That sentence read *"every surface already reports"* until
 *   `4d` (`#1413`) and was true of two of the four: a parameter row and a header row hold their
 *   root in `mappingSource`, while an ORTB body leaf and a shared expression hold theirs inside
 *   the expression and reported nothing at all. Both read it through
 *   {@link rootCalculatedPath} now, so the claim is one a reader can check.
 * - **A shared expression's findings are its author's** (`#1328`). The walk does not enter a
 *   reference; the library page runs the same function over the expression it is editing.
 */

import type { FindingKind } from '@finding-kind';
import { conditionRhsShape, operationArity, type MacroExpression } from '../../../types/decorator-expression';
import { comparisonWhenUnresolved } from '@unresolved-comparison';
import { eachPathProblem, isKnownContextPath } from '../inputs/data-model-schema';
import { joinAtEachPosition, splitAtEachPosition } from '@own-path';

/**
 * **Why a collection operand resolves to nothing, said as the operator reads it** (`CO8`, `2e`).
 * `null` where it will fold members.
 */
export function describeCollectionProblem(subject: string, each: { path: string; member?: string }): string | null {
  const problem = eachPathProblem(each);
  if (!problem) return null;
  const written = joinAtEachPosition(each);
  switch (problem.kind) {
    case 'not-a-list':
      return `${subject} reads ${written}, but ${each.path} is not a list, so [*] has no members to read.`;
    case 'needs-member':
      return `${subject} reads each member of ${each.path}, and each one is a record rather than a value — name the field to read, as in ${joinAtEachPosition({ path: each.path, member: problem.example })}.`;
    case 'unknown-member':
      return describeUnknownPath(subject, written);
  }
}
import type { MappingIssue } from './mapping-issues';
import { describeLocation, walkExpression, type ExpressionLocation } from './expression-walk';

/**
 * **A path the session model does not have resolves to nothing** — the sentence the parameter and
 * header rows have said since `#998`, shared so the same finding one level down reads the same
 * (`#1187`: where two surfaces make the same decision they share the sentence).
 */
/**
 * **`[*]` where it cannot fold, said once for every surface** (`CO8`, `2e`). `null` where the path names
 * no list member at all.
 *
 * Shared because a path is stored two ways and both need it: a parameter or header row keeps a typed
 * path in `mappingSource`, which that row's validator checks, and everything else holds an expression,
 * which the walk below checks. It lived only in the walk first, and Todd's rehearsal found the row
 * form — the one an operator types first — reaching no check.
 */
export function describeEachOutsideCalculation(subject: string, path: string): string | null {
  return splitAtEachPosition(path.trim())
    ? `${subject} reads ${path.trim()}, and [*] — every member of a list — only folds inside a calculation. Use Calculate and put it in as an operand, to add or average the members.`
    : null;
}

/** The operations that fold their operands as numbers — where an empty literal becomes a term. */
const ARITHMETIC = new Set(['add', 'subtract', 'multiply', 'divide', 'avg']);

export function describeUnknownPath(subject: string, path: string): string {
  return `${subject} is mapped to ${path}, which is not a field on the session — it will resolve to nothing.`;
}

/**
 * **Who reports the value's _root_ path** — declared by the surface, because the walk cannot know
 * (`4d`, `#1413`).
 *
 * It is required, and it has three answers rather than a boolean, for the reason `X33a` gives: a
 * surface that genuinely addresses another vocabulary would be making a **false claim** by saying
 * *somebody else checks it*, and a missing check and a false claim are different things. Declaring
 * it produced the audit, which is the point — of the five callers, **two** were reporting nothing
 * about their own root while the walk's docblock said every surface did.
 */
export type RootPathOwner =
  /** The surface's row validator checks the root path — a parameter row, a header row. */
  | 'row'
  /** Nothing else does, so the walk reports it — an ORTB body leaf, a shared expression. */
  | 'walk'
  /**
   * The root addresses a vocabulary this check does not speak, so it has nothing to say — a
   * response mapping's source is a path into the **response**, graded by its own catalog and its
   * own version rules.
   */
  | 'other-vocabulary';

/**
 * The structural findings inside one row's expression.
 *
 * @param subject how the row is named in a sentence, in its surface's own quoting — `"pubid"`, or
 *                `` `imp.bidfloor` ``. The location follows it. `null` where the sentence is read
 *                beside the expression itself and has no row to name — the library page.
 * @param ref     the row's handle, exactly as the surface's other findings carry it, so a Fix routes
 *                to the same row they do.
 * @param root    who reports the **root** path — see {@link RootPathOwner}.
 */
export function expressionStructureIssues(
  expression: MacroExpression | undefined,
  subject: string | null,
  ref: string,
  root: RootPathOwner,
): MappingIssue[] {
  const issues: MappingIssue[] = [];
  const push = (at: ExpressionLocation, kind: FindingKind, message: string) =>
    issues.push({ kind, message: subject === null ? message : `${subject}: ${message}`, ref, at });

  /**
   * **The root, said the way the row surfaces say it** — the whole sentence rather than the
   * location-prefixed form the walk uses below, because there is no location to name: this *is*
   * the row's mapping, and `"pubid" is mapped to foo.bar…` is exactly what a parameter row has
   * said since `#998`.
   */
  const rootPath = rootCalculatedPath(expression);
  if (root === 'walk' && rootPath && !isKnownContextPath(rootPath)) {
    issues.push({
      kind: 'unresolved',
      message: describeUnknownPath(subject ?? 'This expression', rootPath),
      ref,
      at: [],
    });
  }
  /** The location as the start of a sentence, or the root's name where there is no location. */
  const where = (at: ExpressionLocation) => (at.length === 0 ? 'The calculation' : describeLocation(at));

  walkExpression(expression, {
    value: (node, at) => {
      /**
       * **`[*]` folds only where a calculation's operand holds it** (`CO8`, `2e`). Anywhere else — the
       * row's own value, a branch, a comparison — the line keeps it as the path it was typed as, and a
       * path cannot stand on *every* member, so it resolves to nothing. Said at the root too, whoever
       * owns the root's other checks: this one is about the engine, not the vocabulary. The redirect is
       * reachable from every one of those slots — each carries `Calculate…`.
       */
      const each = node.type === 'calculated' ? describeEachOutsideCalculation(where(at), node.path) : null;
      if (each) {
        push(at, 'unresolved', each);
        return;
      }
      if (node.type === 'calculated' && at.length > 0) {
        const path = node.path.trim();
        if (path === '') {
          push(at, 'missing-value', `${where(at)} has no path, so it resolves to nothing.`);
        } else if (!isKnownContextPath(path)) {
          push(at, 'unresolved', describeUnknownPath(where(at), path));
        }
      }
      /**
       * **Zero operands is reachable only from a stored document** — the editor never removes the
       * last one — and the engine answers it with the scope's default every time. A producer takes
       * none by design.
       */
      if (node.type === 'computed' && operationArity(node.operation) === 'combines' && node.operands.length === 0) {
        push(at, 'missing-value', `${where(at)} has no operands, so it always takes its default.`);
      }
      /**
       * **An empty literal is a term in arithmetic, and it is `0`** (found building `2e`). A combining
       * operation opens with two rows, so `avg` over a list plus the row nobody filled averages in a
       * zero and reports a wrong number as resolved. Not refused — an authored empty value is an
       * answer (`#1300`) — but said, because nobody types an empty row meaning *zero*.
       */
      if (node.type === 'computed' && ARITHMETIC.has(node.operation ?? 'concat')) {
        node.operands.forEach((operand, index) => {
          if (operand.type === 'static' && String(operand.value).trim() === '') {
            push(
              [...at, { kind: 'operand', index }],
              'missing-value',
              `${describeLocation([...at, { kind: 'operand', index }])} is empty, so it counts as 0 in the ${node.operation}. Remove it, or give it a value.`,
            );
          }
        });
      }
    },
    collection: (operand, at) => {
      const message = describeCollectionProblem(where(at), operand);
      if (message) push(at, 'unresolved', message);
    },

    caseConditions: (list, at) => {
      // An empty list, from a stored document: `all` of nothing holds and `any` of nothing does not
      // (`evaluateCriteriaNode`), so the case is either every request or none.
      if (list === null || list.conditions.length > 0) return;
      push(
        at,
        'missing-value',
        list.mode === 'all'
          ? `${describeLocation(at)} has no conditions, so it always applies — no case after it, and not Otherwise, is ever reached.`
          : `${describeLocation(at)} has no conditions, so it never applies.`,
      );
    },

    condition: ({ operator, left, right, values }, at) => {
      /**
       * **What the comparison answers with a side missing is the engine's rule, read from the one
       * place it is written** (`comparisonWhenUnresolved`, `#1384`): `neq` holds, everything else
       * fails closed. The presence pair is not that rule — a missing value tested is exactly what
       * `notExists` asks about.
       */
      const outcome = (answer: boolean) => (answer ? 'always holds' : 'never holds');
      if (left === undefined) {
        const answer = comparisonWhenUnresolved(operator) ?? operator === 'notExists';
        push(at, 'missing-value', `${describeLocation(at)} has no value to test, so it ${outcome(answer)}.`);
      }
      const shape = conditionRhsShape(operator);
      /**
       * **An unset *Compare with* is not an empty value.** It compares as a side that does not
       * resolve, so `eq` never holds and `neq` always does — where an empty value would compare as
       * `''`. The editor draws it as unset for that reason (`#1384`, option 2), and this says what
       * it does.
       */
      if (shape === 'scalar' && right === undefined && values === undefined) {
        push(
          at,
          'missing-value',
          `${describeLocation(at)} has no value to compare with, so it ${outcome(comparisonWhenUnresolved(operator) === true)}.`,
        );
      }
      if (shape === 'list' && right === undefined && (values === undefined || values.length === 0)) {
        push(at, 'missing-value', `${describeLocation(at)} tests against an empty list, so it never holds.`);
      }
    },
  });

  return issues;
}

/**
 * **The context path a value reads at its root, or `null` where its root is not a path** (`4d`,
 * `#1413`).
 *
 * The walk above skips the root deliberately — *the root is the row's business* — and that claim
 * was true of two surfaces out of four. A parameter row and a header row hold their root path in
 * `mappingSource` and check it; an ORTB body leaf and a shared expression hold theirs **inside**
 * the expression, so the check every plane has had since `#998` had nothing to read it from and
 * a mistyped path on a body leaf was silent.
 *
 * So the root is read from one place rather than each surface reaching into
 * `expression.type === 'calculated'` for itself: it is the same question the picker, the summary
 * and the row label each already answer their own way, and a fifth private copy is how the next
 * surface comes to disagree about what a row maps.
 */
export function rootCalculatedPath(expression: MacroExpression | undefined | null): string | null {
  if (!expression || expression.type !== 'calculated') return null;
  const path = expression.path.trim();
  return path === '' ? null : path;
}
